import React, { useState } from 'react';
import Icon from 'components/AppIcon';

export default function ProfileSection({ showToast }) {
  const [firstName, setFirstName] = useState('Sarah');
  const [lastName, setLastName] = useState('Mitchell');
  const [jobTitle, setJobTitle] = useState('Platform Administrator');
  const [organisation, setOrganisation] = useState('SeedDrive');
  const [phone, setPhone] = useState('+44 20 7946 0001');
  const [linkedin, setLinkedin] = useState('https://linkedin.com/in/sarah-mitchell');
  const [bio, setBio] = useState('Infrastructure equity specialist with 12 years of experience in institutional investment platforms.');
  const [timezone, setTimezone] = useState('Europe/London');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');

  const inputCls = 'w-full px-3 py-2 border border-slate-200 rounded-xl text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-[#3b4c4c]/30 focus:border-[#3b4c4c] transition-colors';

  const getPwdStrength = (pwd) => {
    if (!pwd) return { label: '', color: '', width: '0%' };
    let score = 0;
    if (pwd?.length >= 8) score++;
    if (/[A-Z]/?.test(pwd)) score++;
    if (/[0-9]/?.test(pwd)) score++;
    if (/[^A-Za-z0-9]/?.test(pwd)) score++;
    const levels = [
      { label: 'Weak', color: 'bg-red-400', width: '25%' },
      { label: 'Fair', color: 'bg-amber-400', width: '50%' },
      { label: 'Good', color: 'bg-blue-400', width: '75%' },
      { label: 'Strong', color: 'bg-green-500', width: '100%' },
    ];
    return levels?.[Math.min(score - 1, 3)] || levels?.[0];
  };

  const strength = getPwdStrength(newPwd);

  return (
    <div>
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
        <h3 className="text-base font-semibold text-slate-900 mb-1">Personal Information</h3>
        <p className="text-sm text-slate-500 mb-5">Update your profile details and contact information.</p>
        <div className="border-t border-slate-100 pt-5">

          {/* Avatar */}
          <div className="flex items-center gap-5 mb-6">
            <div className="w-16 h-16 rounded-2xl bg-[#3b4c4c] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-xl">SM</span>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-700">Profile Photo</p>
              <p className="text-xs text-slate-400 mb-2">PNG or JPG, max 5MB</p>
              <button
                onClick={() => showToast('Photo updated')}
                className="text-xs font-semibold text-[#3b4c4c] hover:underline"
              >
                Change Photo
              </button>
            </div>
          </div>

          {/* Name row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">First Name</label>
              <input type="text" value={firstName} onChange={e => setFirstName(e?.target?.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Last Name</label>
              <input type="text" value={lastName} onChange={e => setLastName(e?.target?.value)} className={inputCls} />
            </div>
          </div>

          {/* Email */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
            <div className="flex items-center gap-3">
              <input type="email" value="sarah.mitchell@seeddrive.demo" readOnly className={`${inputCls} flex-1 opacity-70`} />
              <button onClick={() => showToast('Verification email sent')} className="text-xs font-semibold text-[#3b4c4c] hover:underline whitespace-nowrap">Change email</button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Job Title</label>
              <input type="text" value={jobTitle} onChange={e => setJobTitle(e?.target?.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Organisation</label>
              <input type="text" value={organisation} onChange={e => setOrganisation(e?.target?.value)} className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone Number <span className="text-slate-400 font-normal">(optional)</span></label>
              <input type="tel" value={phone} onChange={e => setPhone(e?.target?.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">LinkedIn URL <span className="text-slate-400 font-normal">(optional)</span></label>
              <input type="url" value={linkedin} onChange={e => setLinkedin(e?.target?.value)} className={inputCls} />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Bio <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <textarea
              value={bio}
              onChange={e => bio?.length <= 250 && setBio(e?.target?.value)}
              rows={3}
              maxLength={250}
              className={`${inputCls} resize-none`}
            />
            <p className="text-xs text-slate-400 mt-1 text-right">{bio?.length}/250</p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Timezone Override</label>
            <select value={timezone} onChange={e => setTimezone(e?.target?.value)} className={inputCls}>
              <option value="Europe/London">Europe/London (GMT+0)</option>
              <option value="Europe/Paris">Europe/Paris (GMT+1)</option>
              <option value="America/New_York">America/New_York (GMT-5)</option>
              <option value="Asia/Singapore">Asia/Singapore (GMT+8)</option>
            </select>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => showToast('Profile saved')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-[#3b4c4c] hover:bg-[#162d4a] transition-colors shadow-sm"
            >
              <Icon name="Save" size={15} />
              Save Profile
            </button>
          </div>
        </div>
      </div>
      {/* Password & Security */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-1">Password & Security</h3>
        <p className="text-sm text-slate-500 mb-5">Manage your password and two-factor authentication.</p>
        <div className="border-t border-slate-100 pt-5 flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => setShowPasswordModal(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-slate-200 text-slate-700 bg-white hover:bg-slate-50 transition-colors"
          >
            <Icon name="KeyRound" size={15} />
            Change Password
          </button>
          <button
            onClick={() => showToast('2FA setup — coming soon')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-slate-200 text-slate-500 bg-slate-50 hover:bg-slate-100 transition-colors"
          >
            <Icon name="Smartphone" size={15} />
            Enable Two-Factor Authentication
            <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-slate-200 text-slate-500">Coming soon</span>
          </button>
        </div>
      </div>
      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-semibold text-slate-900">Change Password</h3>
              <button onClick={() => setShowPasswordModal(false)} className="p-1.5 rounded-lg hover:bg-slate-100">
                <Icon name="X" size={16} color="#94a3b8" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Current Password</label>
                <input type="password" value={currentPwd} onChange={e => setCurrentPwd(e?.target?.value)} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">New Password</label>
                <input type="password" value={newPwd} onChange={e => setNewPwd(e?.target?.value)} className={inputCls} />
                {newPwd && (
                  <div className="mt-2">
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${strength?.color}`} style={{ width: strength?.width }} />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Strength: <span className="font-medium">{strength?.label}</span></p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Confirm New Password</label>
                <input type="password" value={confirmPwd} onChange={e => setConfirmPwd(e?.target?.value)} className={inputCls} />
                {confirmPwd && newPwd !== confirmPwd && (
                  <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                )}
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button onClick={() => setShowPasswordModal(false)} className="px-4 py-2 rounded-xl text-sm font-medium border border-slate-200 text-slate-600 hover:bg-slate-50">
                Cancel
              </button>
              <button
                onClick={() => {
                  if (newPwd === confirmPwd && newPwd) {
                    setShowPasswordModal(false);
                    setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
                    showToast('Password updated');
                  }
                }}
                disabled={!currentPwd || !newPwd || newPwd !== confirmPwd}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-[#3b4c4c] hover:bg-[#162d4a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
